import Plotly, { PlotlyHTMLElement } from '@newcrom/plotly.js';
import { Callback, Nullable } from '@/types/utility';
import { generateId } from '@/utils/generateId';
import { convertCoordsToPath } from '@/utils/svgHelpers';
import { throwError } from '@/utils/errorHelpers';
import mitt, { Emitter, Handler } from 'mitt';
import {
  ChartEventListeners,
  Config,
  Coords,
  DataExtended,
  DetectionTime,
  Layout,
  Measurement,
  Peak,
  Shape,
  Sizes,
  ZoomBounds,
} from '@/uikitProject/charts/new/private/types';
import {
  AXIS_HOVER_FORMAT,
  COLORS,
  CONFIG,
  DATA_HOVER_TEMPLATE_FOR_CHROMATOGRAM,
  MESSAGES,
  SECONDS_IN_MINUTE,
  SHAPE_IDS,
  SIZES,
} from '@/uikitProject/charts/new/private/constants';
import {
  addStyleSheetForPeakHighlighting,
  createMessageOverlay,
  createZoomActivationOverlay,
  findYBetweenTwoPoints,
  getFilteredData,
  getHoverMessages,
  getMeasurementPart,
  getStartAndEndXValues,
  hasSamePeaks,
} from '@/uikitProject/charts/new/private/helpers';
import _ from 'lodash';
import { sendDebugEvent } from '@/utils/sentryHelpers';
import { generateRange, generateSkewedGaussianRange } from '@/utils/gaussianHelpers';
import { PEAK_ESTIMATED_SHAPES } from '@/constants/peaks/estimatedShapes';
import hash from 'object-hash';
import { persistentStorage, PersistentStorageKeys } from '@/utils/persistentStorage';
import { consoleHelpers } from '@/utils/logHelpers';

type Events = {
  deletePeaks: number[];
  addPeak: {
    startMau: number;
    endMau: number;
    startMinutes: number;
    endMinutes: number;
  };
  baseline: [time: number, mau: number][];
  zoom: Nullable<ZoomBounds>;
  highlightPeak: Nullable<number>;
  zoomActivation: void;
};

export enum Modes {
  DEFAULT,
  ADD_PEAK,
  REMOVE_PEAK,
  BASELINE,
}

type InitialOptions = Readonly<{
  container: HTMLElement;
  measurements: Measurement[];
  expectedDurationSeconds: number;
  detectionTime: Nullable<DetectionTime>;
  isInProgress: boolean;
  isFetchingData: boolean;
  isShowBaseline: boolean;
  isShowPeaks: boolean;
  widthPx?: Nullable<number>;
  hasTooltipOnHover: boolean;
  hasGridLines: boolean;
  zoomBounds: Nullable<ZoomBounds>;
  sizes?: Sizes;
  hasZoomActivationOverlay: boolean;
  creationData: Date;
}>;

type RenderOptions = {
  highlightedPeakIds?: Set<number>;
  // this.chart.layout.shapes can be undefined. There is the error in Plotly typings
  shapes: Nullable<Shape[]>;
  hasZoom: boolean;
  isShowBaseline: boolean;
  isShowPeaks: boolean;
  isShowDetectionZone: boolean;
  isInProgress: boolean;
  isFetchingData: boolean;
  detectionTime: Nullable<DetectionTime>;
  expectedDurationSeconds: number;
  widthPx: Nullable<number>;
  isShowEstimatedGaussians: boolean;
  hasTooltipOnHover: boolean;
  hasGridLines: boolean;
  overlayMessage: Nullable<string>;
};

type Actions = {
  resetBaseline: Nullable<Callback>;
  removeLastBaselineSegment: Nullable<Callback>;
  setIsShowBaseline: Callback<[isShowBaseline: boolean]>;
  setIsShowPeaks: Callback<[isShowPeaks: boolean]>;
  setIsShowDetectionZone: Callback<[isShowDetectionZone: boolean]>;
  setPeakHighlighting: Callback<[peakId: number, isHighlighted: boolean]>;
  setDetectionTime: Callback<[detectionTime: Nullable<DetectionTime>]>;
  setIsInProgress: Callback<[isInProgress: boolean]>;
  setIsFetchingData: Callback<[setIsFetchingData: boolean]>;
  setExpectedDurationSeconds: Callback<[expectedDurationSeconds: number]>;
  setWidth: Callback<[widthPx: number]>;
  setHasTooltipOnHover: Callback<[hasTooltipOnHover: boolean]>;
  setHasGridLines: Callback<[hasGridLines: boolean]>;
  setZoom: Callback<[bounds: ZoomBounds]>;
  setOverlayMessage: Callback<[message: Nullable<string>]>;
  zoomToDetectionTime: Callback;
  showBaselinePreview: Callback<[coords: Array<[x: number, y: number]>]>;
};

type DataForRender = {
  data: DataExtended[];
  layout: Layout;
  isInProgress: boolean;
  messageOverlay: Nullable<string>;
};

const throwErrorElementNotFound = () => throwError('Element was not found!');

const IS_SHOW_LOGS = false;
const LOG_PREFIX = '[ChromatogramDrawer]';
const log = (...args: unknown[]) => {
  if (IS_SHOW_LOGS) {
    consoleHelpers.debug(LOG_PREFIX, `- ${new Date().getTime()} -`, ...args);
  }
};

export class ChromatogramDrawer {
  private _chart: Nullable<PlotlyHTMLElement> = null;
  private mode: Modes = Modes.DEFAULT;
  private config: Nullable<Config> = CONFIG;
  private sizes: Sizes;
  private renderOptions: RenderOptions = {
    highlightedPeakIds: new Set(),
    shapes: [],
    hasZoom: true,
    isShowBaseline: false,
    isShowPeaks: false,
    isShowDetectionZone: true,
    isInProgress: false,
    isFetchingData: false,
    detectionTime: null,
    expectedDurationSeconds: 0,
    widthPx: null,
    isShowEstimatedGaussians: true,
    hasTooltipOnHover: false,
    hasGridLines: false,
    overlayMessage: null,
  };

  private uiRevisionVersion: number =
    persistentStorage.get(PersistentStorageKeys.CHROMATOGRAM_UI_REVISION) ?? 0;

  private disableCurrentMode: Nullable<Callback> = null;
  private readyForRenderMeasurements: Measurement[];
  private renderedMeasurements: Nullable<Measurement[]> = null;

  private mediaQueryIsMobileSize: MediaQueryList;

  private readonly messageOverlay = createMessageOverlay();
  private zoomActivationOverlay: Nullable<ReturnType<typeof createZoomActivationOverlay>>;

  private emitter: Emitter<Events>;

  private resolveInitializationPromise: Nullable<Callback> = null;
  public readonly initializationPromise: Promise<unknown> = new Promise((resolve) => {
    this.resolveInitializationPromise = resolve;
  });

  private readonly creationDate: Date;

  /**
   * The only way to update measurements after initialization
   */
  public refresh: Callback<[measurements?: Measurement[]]> = (
    measurements = this.readyForRenderMeasurements,
  ) => {
    log('refresh called', { measurementsLength: measurements?.length });
    // It is necessary to override the variable, as the current method can be called several times in a row without arguments.
    this.readyForRenderMeasurements = measurements;
    this.refreshWithDebounce(measurements);
  };

  /**
   * Debounced version of refreshImmediately function
   */
  private refreshWithDebounce: Callback<[measurements?: Measurement[]]> = _.debounce(
    this.refreshImmediately.bind(this),
    100,
  );

  // TODO make private and add public methods?
  public actions: Actions = {
    removeLastBaselineSegment: null,
    resetBaseline: null,
    setIsShowBaseline: (isShowBaseline: boolean) => {
      log('action: setIsShowBaseline', {
        isShowBaseline,
        previous: this.renderOptions.isShowBaseline,
      });
      this.renderOptions.isShowBaseline = isShowBaseline;
      this.refresh();
    },
    setIsShowPeaks: (isShowPeaks: boolean) => {
      log('action: setIsShowPeaks', { isShowPeaks, previous: this.renderOptions.isShowPeaks });
      this.renderOptions.isShowPeaks = isShowPeaks;
      this.refresh();
    },
    setIsShowDetectionZone: (isShowDetectionZone: boolean) => {
      log('action: setIsShowDetectionZone', {
        isShowDetectionZone,
        previous: this.renderOptions.isShowDetectionZone,
      });
      this.renderOptions.isShowDetectionZone = isShowDetectionZone;
      this.refresh();
    },
    setPeakHighlighting: (peakId: number, isHighlighted: boolean) => {
      log('action: setPeakHighlighting', { peakId, isHighlighted });
      isHighlighted
        ? this.renderOptions.highlightedPeakIds?.add(peakId)
        : this.renderOptions.highlightedPeakIds?.delete(peakId);
      this.refresh();
    },
    setDetectionTime: (detectionTime: Nullable<DetectionTime>) => {
      log('action: setDetectionTime', {
        detectionTime,
        previous: this.renderOptions.detectionTime,
      });
      this.renderOptions.detectionTime = detectionTime;
      this.refresh();
    },
    setIsInProgress: (isInProgress: boolean) => {
      log('action: setIsInProgress', { isInProgress, previous: this.renderOptions.isInProgress });
      this.renderOptions.isInProgress = isInProgress;
      this.refresh();
    },
    setIsFetchingData: (isFetchingData: boolean) => {
      log('action: setIsFetchingData', {
        isFetchingData,
        previous: this.renderOptions.isFetchingData,
      });
      this.renderOptions.isFetchingData = isFetchingData;
      this.refresh();
    },
    setExpectedDurationSeconds: (expectedDurationSeconds: number) => {
      log('action: setExpectedDurationSeconds', {
        expectedDurationSeconds,
        previous: this.renderOptions.expectedDurationSeconds,
      });
      this.renderOptions.expectedDurationSeconds = expectedDurationSeconds;
      this.refresh();
    },
    setWidth: (widthPx: number) => {
      log('action: setWidth', { widthPx, previous: this.renderOptions.widthPx });
      this.renderOptions.widthPx = widthPx;
      this.refresh();
    },
    setHasTooltipOnHover: (hasTooltipOnHover: boolean) => {
      log('action: setHasTooltipOnHover', {
        hasTooltipOnHover,
        previous: this.renderOptions.hasTooltipOnHover,
      });
      this.renderOptions.hasTooltipOnHover = hasTooltipOnHover;
      this.refresh();
    },
    setHasGridLines: (hasGridLines: boolean) => {
      log('action: setHasGridLines', { hasGridLines, previous: this.renderOptions.hasGridLines });
      this.renderOptions.hasGridLines = hasGridLines;
      this.refresh();
    },
    setZoom: (bounds: ZoomBounds) => {
      log('action: setZoom', bounds);
      this.zoom(bounds);
    },
    setOverlayMessage: (message: Nullable<string>) => {
      log('action: setOverlayMessage', { message, previous: this.renderOptions.overlayMessage });
      this.renderOptions.overlayMessage = message;
      this.refresh();
    },
    zoomToDetectionTime: () => {
      log('action: zoomToDetectionTime start');
      if (this.renderOptions.detectionTime == null) {
        throw new Error('Detection time is not defined!');
      }

      const zoomBounds = {
        x0: this.renderOptions.detectionTime.start ?? 0,
        x1:
          this.renderOptions.detectionTime.end ??
          this.renderOptions.expectedDurationSeconds / SECONDS_IN_MINUTE,
        y0: this.bounds.y0,
        y1: this.bounds.y1,
      };
      log('action: zoomToDetectionTime bounds', zoomBounds);
      this.zoom(zoomBounds);
    },
    showBaselinePreview: (coords: Array<[x: number, y: number]>) => {
      log('action: showBaselinePreview', { coordsLength: coords.length });
      const firstCoord = coords.at(0);
      const lastCoord = coords.at(-1);

      if (firstCoord != null && lastCoord != null) {
        const extendedToEdgesCoords = [
          [this.bounds.x0, firstCoord[1]] as const,
          ...coords,
          [this.bounds.x1, lastCoord[1]] as const,
        ];
        log('action: showBaselinePreview extended coords', {
          extendedLength: extendedToEdgesCoords.length,
        });

        const shape = this.getBaselineShape(extendedToEdgesCoords);
        this.addOrEditShape(shape);
        this.refresh();
      }
    },
  };

  constructor(initialOptions: InitialOptions) {
    log('constructor', initialOptions);
    this.readyForRenderMeasurements = initialOptions.measurements;
    this.renderOptions.isInProgress = initialOptions.isInProgress;
    this.renderOptions.isFetchingData = initialOptions.isFetchingData;
    this.renderOptions.detectionTime = initialOptions.detectionTime;
    this.renderOptions.expectedDurationSeconds = initialOptions.expectedDurationSeconds;
    this.renderOptions.isShowBaseline = initialOptions.isShowBaseline;
    this.renderOptions.isShowPeaks = initialOptions.isShowPeaks;
    this.renderOptions.widthPx = initialOptions.widthPx;
    this.renderOptions.hasTooltipOnHover = initialOptions.hasTooltipOnHover;
    this.renderOptions.hasGridLines = initialOptions.hasGridLines;
    this.sizes = initialOptions.sizes ?? SIZES;
    this.emitter = mitt<Events>();
    this.mediaQueryIsMobileSize = window.matchMedia(
      `(max-width: ${this.sizes.mobileScreenSizePx}px)`,
    );
    this.creationDate = initialOptions.creationData;

    this.initPlotly(initialOptions);
  }

  public on<Key extends keyof Events>(type: Key, handler: Handler<Events[Key]>) {
    log('on: registering event listener', { type });
    this.emitter.on(type, handler);
  }

  private async initPlotly({
    container,
    measurements,
    zoomBounds,
    hasZoomActivationOverlay,
  }: InitialOptions) {
    log('initPlotly start', { measurementsCount: measurements.length, zoomBounds });
    container.style.position = 'relative';

    const { data, layout, isInProgress, messageOverlay: message } = await this.getDataForRender(
      measurements,
    );
    log('initPlotly: got data for render', { dataLength: data.length, isInProgress, message });

    if (hasZoomActivationOverlay) {
      log('initPlotly: creating zoom activation overlay');
      this.zoomActivationOverlay = createZoomActivationOverlay();
      this.zoomActivationOverlay.onActive = () => {
        log('zoomActivationOverlay: onActive');
        container.style.touchAction = 'none';
        this.emitter.emit('zoomActivation');
      };
      this.zoomActivationOverlay.onDisable = () => {
        log('zoomActivationOverlay: onDisable');
        container.style.touchAction = 'none';
        this.emitter.emit('zoomActivation');
      };

      container.append(this.zoomActivationOverlay.element);
    } else {
      log('initPlotly: setting touch action to none');
      container.style.touchAction = 'none';
    }

    // messageOverlay should be after zoomActivationOverlay
    log('initPlotly: editing message overlay', { message, isInProgress });
    this.messageOverlay.edit(message, isInProgress);
    container.append(this.messageOverlay.element);

    log('initPlotly: adding peak highlighting stylesheet');
    addStyleSheetForPeakHighlighting();

    log('initPlotly: calling Plotly.newPlot');
    this.chart = await Plotly.newPlot(container, data, layout, this.config ?? undefined);

    this.renderedMeasurements = this.readyForRenderMeasurements;

    log('initPlotly: Plotly.newPlot completed');

    if (zoomBounds) {
      log('initPlotly: applying initial zoom', zoomBounds);
      this.zoom(zoomBounds);
    }

    // Восстановлено: слушатель изменения области графика
    log('initPlotly: setting up plotly_relayout listener');
    this.chart.on('plotly_relayout', () => {
      const { xaxis, yaxis } = this.chart.layout;

      /**
       * I use the autorange property to define a scale reset for yaxis.
       */
      // TODO I found an issue where you can pass null into a variable marked as a number if you use this construct.
      // xaxis.autorange === true ? null : xaxis.range?.[0]
      // TODO As far as I remember, there is an issue if you change the zoom by dragging the axes; in this case, additional verification is necessary.

      this.emitter.emit(
        'zoom',
        xaxis.autorange === true && yaxis.autorange === true
          ? null
          : {
              x0: xaxis.range?.[0],
              x1: xaxis.range?.[1],
              y0: yaxis.range?.[0],
              y1: yaxis.range?.[1],
            },
      );

      log('plotly_relayout', {
        xRange: xaxis.range,
        yRange: yaxis.range,
        autorangeX: xaxis.autorange,
        autorangeY: yaxis.autorange,
      });
    });

    // To use the same cursor for all cases and avoid blinking when drawing lines
    log('initPlotly: setting cursors to crosshair');
    this.chart.style.cursor = 'crosshair';
    this.dragLayer.style.cursor = 'crosshair';

    log('initPlotly: setting up media query listener');
    this.mediaQueryIsMobileSize.onchange = () => {
      log('media query changed, calling refresh');
      this.refresh();
    };

    // TODO use approach with a public initPlotly method?
    log('initPlotly: resolving initialization promise');
    this.resolveInitializationPromise?.();

    log('initPlotly finished');
  }

  public destroy() {
    log('destroy called');
    // TODO check all listeners and remove them
    this.disableCurrentMode?.();
    this.mediaQueryIsMobileSize.onchange = null;
    log('destroy finished');
  }

  private get chart() {
    log('getter: chart accessed');
    return this._chart ?? throwError('Chart was not initialized!');
  }
  private set chart(chartElement: PlotlyHTMLElement) {
    log('setter: chart set');
    this._chart = chartElement;
  }

  private get layout() {
    log('getter: layout accessed');
    return this.chart.layout;
  }

  private get dragLayer(): SVGElement {
    log('getter: dragLayer accessed');
    const dragElement =
      this.chart.querySelector(`.nsewdrag.drag`) ?? throwError('Drag element was not found!');

    if (dragElement instanceof SVGElement) {
      return dragElement;
    }

    throw new Error('Drag layer is not SVG element!');
  }

  private get data(): DataExtended[] {
    log('getter: data accessed');
    // TODO data is any in library typings. I can add a zod checking in the future
    const data: DataExtended[] = this.chart.data;
    return data.filter((item) => !item.isBaseline);
  }

  private get singleMeasurement(): DataExtended {
    log('getter: singleMeasurement accessed');
    if (this.data.length === 0) {
      throw new Error('There are no measurements!');
    }

    if (this.data.length === 1) {
      return this.data[0];
    }

    throw new Error('Chromatogram is in multiple mode!');
  }

  private get bounds(): ZoomBounds {
    log('getter: bounds accessed');
    const [minutesMin, minutesMax] = this.layout.xaxis.range ?? [];
    const [mauMin, mauMax] = this.layout.yaxis.range ?? [];

    if ([minutesMin, minutesMax, mauMin, mauMax].every((value) => typeof value === 'number')) {
      const bounds = {
        x0: minutesMin,
        x1: minutesMax,
        y0: mauMin,
        y1: mauMax,
      };
      log('getter: bounds calculated', bounds);
      return bounds;
    }
    throw new Error('Bounds are not found!');
  }

  /**
   * Base method. A debounced version is preferred
   */
  private async refreshImmediately(measurements = this.readyForRenderMeasurements) {
    await this.initializationPromise;

    log('refreshImmediately start', { measurementsLength: measurements.length });

    const isFirstRender = this.renderedMeasurements == null;
    const isSingleMode = measurements.length === 1;
    const [renderedFirstMeasurement] = this.renderedMeasurements ?? [];
    const [firstMeasurement] = measurements;

    log('refreshImmediately: render state', {
      isFirstRender,
      isSingleMode,
      hasFirstMeasurement: firstMeasurement !== null,
    });

    const renderedPostprocessingsHash =
      renderedFirstMeasurement != null ? hash(renderedFirstMeasurement?.postprocessings ?? '') : '';
    const currentPostprocessingsHash =
      firstMeasurement != null ? hash(firstMeasurement?.postprocessings ?? '') : '';
    const wasJustPostprocessed =
      !isFirstRender && isSingleMode && currentPostprocessingsHash !== renderedPostprocessingsHash;
    if (wasJustPostprocessed) {
      log('refreshImmediately: was just postprocessed, incrementing UI revision');
      this.incrementUiRevisionVersion();
    }

    log('refreshImmediately: updating ready measurements');
    this.readyForRenderMeasurements = measurements;

    const { data, layout, isInProgress, messageOverlay: message } = await this.getDataForRender(
      measurements,
    );
    log('refreshImmediately: got data for render', {
      dataLength: data.length,
      isInProgress,
      message,
    });

    log('refreshImmediately: editing message overlay');
    this.messageOverlay.edit(message, isInProgress);

    // To display all edges of peaks
    const _hasSamePeaks =
      isSingleMode &&
      renderedFirstMeasurement != null &&
      hasSamePeaks(firstMeasurement, renderedFirstMeasurement);
    log('refreshImmediately: checking same peaks', { _hasSamePeaks });

    log('refreshImmediately: calling Plotly.react');

    Plotly.react(
      this.chart,
      data,
      layout,
      {
        ...this.config,
        doubleClick: this.renderOptions.hasZoom ? this.config?.doubleClick : false,
      },
      !_hasSamePeaks,
    );

    log('refreshImmediately: updating rendered measurements');
    this.renderedMeasurements = this.readyForRenderMeasurements;

    log('refreshImmediately finished');
  }

  // TODO only actions should be public
  public setMode(mode: Modes) {
    log('setMode', { previousMode: this.mode, newMode: mode });
    this.disableCurrentMode?.();

    this.mode = mode;

    switch (mode) {
      case Modes.DEFAULT:
        this.enableZoomMode();
        break;
      case Modes.ADD_PEAK:
        this.enableAddPeakMode();
        break;
      case Modes.REMOVE_PEAK:
        this.enableRemovePeakMode();
        break;
      case Modes.BASELINE:
        this.enableBaselineMode();
        break;
    }
  }

  private incrementUiRevisionVersion() {
    log('incrementUiRevisionVersion: before', this.uiRevisionVersion);
    this.uiRevisionVersion += 1;
    persistentStorage.set(PersistentStorageKeys.CHROMATOGRAM_UI_REVISION, this.uiRevisionVersion);
    log('incrementUiRevisionVersion: after', this.uiRevisionVersion);
  }

  private async getDataForRender(measurements: Measurement[]): Promise<DataForRender> {
    log('getDataForRender start', { measurementsLength: measurements.length });
    const {
      isShowBaseline,
      isShowPeaks,
      detectionTime,
      expectedDurationSeconds,
      shapes: _shapes,
      hasZoom,
      isInProgress,
      isFetchingData,
      widthPx,
      hasTooltipOnHover,
      hasGridLines,
      isShowDetectionZone,
    } = this.renderOptions;
    log('getDataForRender: render options', {
      isShowBaseline,
      isShowPeaks,
      isShowDetectionZone,
      hasZoom,
      isInProgress,
      isFetchingData,
      hasTooltipOnHover,
      hasGridLines,
    });

    const dataForDrawing: Array<DataExtended> = measurements.map((measurement) => {
      log('getDataForRender: processing measurement', {
        hasData: !!measurement.data,
        dataLength: measurement.data?.length,
        mps: measurement.mps,
      });
      if (measurement.data == null) {
        // TODO Check error and remove
        sendDebugEvent('#2269: Front: Fix sentry issues', [
          ['All measurements', measurements],
          ['Current measurement', [measurement]],
          ['Task', 'https://gitlab.com/hplc-cloud-group/hplc-cloud-server/-/issues/2269'],
          [
            'Sentry issue',
            'https://newcrom.sentry.io/issues/4703701785/?project=6192768&query=is:unresolved&statsPeriod=90d&stream_index=0',
          ],
        ]);

        throw new Error('Data not found!');
      }

      const seconds = measurement.data.length / measurement.mps;
      const minutes = seconds / SECONDS_IN_MINUTE;
      const dx = minutes / measurement.data.length;

      const y = getFilteredData(measurement.data, this.creationDate);

      const x0 = 0;
      log('getDataForRender: measurement processed', {
        seconds,
        minutes,
        dx,
        yLength: y.length,
        x0,
      });

      return {
        y,
        dx,
        x0,
        mps: measurement.mps,
        customdata: getHoverMessages(y, dx, x0, 'mAU'),
        hovertemplate: hasTooltipOnHover ? DATA_HOVER_TEMPLATE_FOR_CHROMATOGRAM : '',
        hoverinfo: hasTooltipOnHover ? 'all' : 'skip',
        mode: 'lines' as const,
        line: {
          color: COLORS.measurements[measurement.meta?.nm ?? 'default'],
        },
      };
    });
    log('getDataForRender: data for drawing created', {
      dataForDrawingLength: dataForDrawing.length,
    });

    const hasOnlyOneMeasurement = measurements.length === 1;
    // TODO add ts index access error
    const [firstMeasurement] = measurements;
    log('getDataForRender: measurement analysis', {
      hasOnlyOneMeasurement,
      hasFirstMeasurement: !!firstMeasurement,
    });

    if (hasOnlyOneMeasurement && isShowBaseline) {
      log('getDataForRender: processing baseline for single measurement');
      const data = firstMeasurement.data;
      const baseline = await firstMeasurement.getBaseline();
      log('getDataForRender: baseline data', {
        dataLength: data?.length,
        baselineLength: baseline?.length,
      });

      if (data.length !== 0 && baseline != null && baseline.length !== 0) {
        const seconds = data.length / firstMeasurement.mps;
        const minutes = seconds / SECONDS_IN_MINUTE;
        const dx = minutes / data.length;

        const y = baseline;
        const x0 = 0;
        log('getDataForRender: adding baseline to data for drawing');

        dataForDrawing.push({
          y,
          dx,
          x0,
          mps: firstMeasurement.mps,
          line: {
            dash: 'dash',
            color: COLORS.baseline,
          },
          customdata: getHoverMessages(y, dx, x0, 'mAU'),
          hovertemplate: hasTooltipOnHover ? DATA_HOVER_TEMPLATE_FOR_CHROMATOGRAM : '',
          hoverinfo: hasTooltipOnHover ? 'all' : 'skip',
          mode: 'lines' as const,
          isBaseline: true,
        });
      }
    }

    const outOfDetectionZoneShapes: Shape[] =
      isShowDetectionZone && detectionTime != null
        ? this.getOutOfDetectionTimeShapes(detectionTime, expectedDurationSeconds)
        : [];
    log('getDataForRender: out of detection zone shapes', {
      shapesLength: outOfDetectionZoneShapes.length,
    });

    const shapes = [
      ...outOfDetectionZoneShapes,
      ...(_shapes != null ? _shapes : []),
      ...(isShowPeaks && hasOnlyOneMeasurement
        ? this.getPeakShapes({
            peaks: [...firstMeasurement.peaks].sort((a, b) => a.apex - b.apex) ?? [],
            data: dataForDrawing[0],
          })
        : []),
    ];

    log('getDataForRender: all shapes combined', {
      totalShapesLength: shapes.length,
      outOfDetectionShapes: outOfDetectionZoneShapes.length,
      existingShapes: _shapes?.length ?? 0,
      peakShapes: isShowPeaks && hasOnlyOneMeasurement ? firstMeasurement.peaks?.length ?? 0 : 0,
    });
    const hasMeasurementFromAnalogDetector = measurements.some(
      (measurement) => measurement.type === 'AD',
    );
    log('getDataForRender: analog detector check', { hasMeasurementFromAnalogDetector });

    // TODO Make some fields not partial for Layout type?
    const layout: Layout = {
      showlegend: false,
      xaxis: {
        autorange: false,
        ticksuffix: ' min',
        showticksuffix: 'last',
        showgrid: hasGridLines,
        zeroline: hasGridLines,
        zerolinecolor: COLORS.zeroline,
        tickfont: {
          color: COLORS.axisLabel,
        },
        range: [0, expectedDurationSeconds / SECONDS_IN_MINUTE],
        uirevision: String(this.uiRevisionVersion),
        hoverformat: AXIS_HOVER_FORMAT,
        fixedrange: !hasZoom,
        rangemode: 'tozero',
        ticks: 'inside',
        // @ts-ignore
        ticklabeloverflow: 'allow',
        tickcolor: COLORS.axisTick,
        showline: true,
      },
      yaxis: {
        ticksuffix: hasMeasurementFromAnalogDetector
          ? measurements.length === 1
            ? ' mV'
            : ''
          : ' mAU',
        showticksuffix: 'last',
        tickprefix: '',
        showtickprefix: 'all',
        showgrid: hasGridLines,
        zeroline: hasGridLines,
        zerolinecolor: COLORS.zeroline,
        ticks: 'outside',
        tickcolor: COLORS.axisTick,
        tickfont: {
          color: COLORS.axisLabel,
        },
        // @ts-ignore
        ticklabeloverflow: 'allow',
        ticklabelposition: 'inside top',
        uirevision: String(this.uiRevisionVersion),
        fixedrange: !hasZoom,
        hoverformat: AXIS_HOVER_FORMAT,
        showline: true,
      },
      shapes,
      dragmode: hasZoom ? 'zoom' : false,
      margin: this.mediaQueryIsMobileSize.matches
        ? this.sizes.margins.mobile
        : this.sizes.margins.desktop,
      width: widthPx ?? undefined,
      height: this.sizes.height,
    };
    log('getDataForRender: layout created', {
      xRange: layout.xaxis?.range,
      margin: layout.margin,
      width: layout.width,
      height: layout.height,
      dragmode: layout.dragmode,
      layout: JSON.stringify(layout, null, 2),
    });

    const hasMoreThan2Coords = dataForDrawing.some((d) => d.y.length > 2);
    log('getDataForRender: data validation', { hasMoreThan2Coords });

    const messageOverlay =
      this.renderOptions.overlayMessage ??
      (isFetchingData
        ? MESSAGES.loading
        : hasMoreThan2Coords
        ? null
        : isInProgress
        ? MESSAGES.loading
        : MESSAGES.noData);
    log('getDataForRender: message overlay determined', { messageOverlay });

    const result = {
      data: dataForDrawing,
      layout,
      isInProgress,
      messageOverlay,
    };
    log('getDataForRender finished', {
      dataLength: result.data.length,
      isInProgress: result.isInProgress,
      messageOverlay: result.messageOverlay,
    });
    return result;
  }

  private disablePlotly() {
    log('disablePlotly called');
    this.renderOptions.hasZoom = false;
    this.refresh();
  }

  private enablePlotly() {
    log('enablePlotly called');
    this.renderOptions.hasZoom = true;
    this.refresh();
  }

  private getXY(e: PointerEvent): Coords {
    log('getXY called', { clientX: e.clientX, clientY: e.clientY });
    const dragLayerClientRect = this.dragLayer.getBoundingClientRect();

    const x = this.chart._fullLayout.xaxis.p2d(e.clientX - dragLayerClientRect.left);
    const y = this.chart._fullLayout.yaxis.p2d(e.clientY - dragLayerClientRect.top);

    const coords = { x, y };
    log('getXY result', coords);
    return coords;
  }

  private isOutOfChart(coords: Coords): boolean {
    log('isOutOfChart called', coords);
    const result =
      coords.x < this.bounds.x0 ||
      coords.x > this.bounds.x1 ||
      coords.y < this.bounds.y0 ||
      coords.y > this.bounds.y1;
    log('isOutOfChart result', { coords, bounds: this.bounds, result });
    return result;
  }

  private addOrEditShape(shape: Shape) {
    log('addOrEditShape called', { shapeName: shape.name, shapeType: shape.type });
    // TODO check
    // this.chart.layout.shapes can be undefined. There is the error in Plotly typings
    if (this.renderOptions.shapes != null) {
      const shapeIndex = this.renderOptions.shapes.findIndex(
        (_shape) => _shape.name === shape.name,
      );
      log('addOrEditShape: shape index', shapeIndex);

      if (shapeIndex !== -1) {
        log('addOrEditShape: updating existing shape');
        this.renderOptions.shapes[shapeIndex] = shape;
      }
      this.renderOptions.shapes =
        shapeIndex !== -1 ? this.renderOptions.shapes : [...this.renderOptions.shapes, shape];
      log('addOrEditShape: shapes count after', this.renderOptions.shapes.length);
    } else {
      log('addOrEditShape: creating first shape');
      this.renderOptions.shapes = [shape];
    }
  }
  private removeShape(shapeName: string) {
    log('removeShape called', { shapeName });
    if (this.renderOptions.shapes != null) {
      const beforeCount = this.renderOptions.shapes.length;
      this.renderOptions.shapes = this.renderOptions.shapes.filter(
        (shape) => shape.name !== shapeName,
      );
      log('removeShape: shapes count', {
        before: beforeCount,
        after: this.renderOptions.shapes.length,
      });
    }
  }

  private enableZoomMode() {
    this.enablePlotly();
    log('enableZoomMode');
  }

  private enableAddPeakMode() {
    this.disablePlotly();
    this.zoomActivationOverlay?.disable();

    log('enableAddPeakMode');
    let isMouseDown = false;
    let peakId = generateId();
    let startPosition: Nullable<Coords> = null;
    log('enableAddPeakMode: initial state', { isMouseDown, peakId, startPosition });

    const onPointerMove = (e: PointerEvent) => {
      log('enableAddPeakMode: onPointerMove', { isMouseDown, startPosition });
      const { x, y } = this.getXY(e);

      if (!startPosition) {
        return;
      }

      const shape: Shape = {
        type: 'line',
        x0: startPosition?.x,
        x1: x,
        y0: startPosition?.y,
        y1: y,
        line: {
          color: COLORS.peakAddLine,
          width: 2,
        },
        name: peakId,
      };
      log('enableAddPeakMode: onPointerMove creating shape', shape);

      this.addOrEditShape(shape);
      this.refreshImmediately();
    };
    const onPointerDown = (e: PointerEvent) => {
      log('enableAddPeakMode: onPointerDown');
      isMouseDown = true;
      e.preventDefault();
      startPosition = this.getXY(e);
      peakId = generateId();
      log('enableAddPeakMode: onPointerDown state', { isMouseDown, startPosition, peakId });
    };
    const onPointerUp = (e: PointerEvent) => {
      log('enableAddPeakMode: onPointerUp', { isMouseDown, startPosition });
      if (isMouseDown && startPosition != null) {
        const upPosition = this.getXY(e);
        const start = startPosition.x < upPosition.x ? startPosition : upPosition;
        const end = startPosition.x < upPosition.x ? upPosition : startPosition;
        log('enableAddPeakMode: onPointerUp positions', { start, end });

        const [xStart, xEnd] = getStartAndEndXValues(this.singleMeasurement);
        log('enableAddPeakMode: onPointerUp measurement bounds', { xStart, xEnd });
        if (xStart == null || xEnd == null) {
          throw new Error('Chart x axis is not found');
        }

        const isClickInsidePlot = xStart <= start.x && xEnd >= end.x;
        log('enableAddPeakMode: onPointerUp click validation', { isClickInsidePlot });
        if (isClickInsidePlot) {
          const peakData = {
            startMau: start.y,
            endMau: end.y,
            startMinutes: start.x,
            endMinutes: end.x,
          };
          log('enableAddPeakMode: onPointerUp emitting addPeak', peakData);
          this.emitter.emit('addPeak', peakData);
        } else {
          log('enableAddPeakMode: onPointerUp click outside plot - ignoring');
          // TODO emit to show a notification
          // TODO add error codes?
          // throw new Error('One of the edge points of the peak is outside the measurement!');
        }

        isMouseDown = false;
        startPosition = null;
        log('enableAddPeakMode: onPointerUp removing shape', { peakId });
        this.removeShape(peakId);
        this.refreshImmediately();
      }
    };

    log('enableAddPeakMode: adding chart listeners');
    const removeListeners = this.addChartListeners({
      onPointerDown,
      onPointerMove,
      onPointerUp,
    });

    this.disableCurrentMode = () => {
      log('enableAddPeakMode: disableCurrentMode called');
      removeListeners();
    };
  }

  private enableRemovePeakMode() {
    this.disablePlotly();
    this.zoomActivationOverlay?.disable();

    log('enableRemovePeakMode');
    let isMouseDown = false;
    let startPosition: Nullable<Coords> = null;
    log('enableRemovePeakMode: initial state', { isMouseDown, startPosition });

    const onPointerDown = (e: PointerEvent) => {
      log('enableRemovePeakMode: onPointerDown');
      e.preventDefault();
      isMouseDown = true;
      startPosition = this.getXY(e);
      log('enableRemovePeakMode: onPointerDown state', { isMouseDown, startPosition });
    };
    const onPointerMove = (e: PointerEvent) => {
      log('enableRemovePeakMode: onPointerMove', { isMouseDown, startPosition });
      const { x } = this.getXY(e);

      if (!startPosition) {
        return;
      }

      const shape = {
        type: 'rect',
        x0: startPosition?.x,
        x1: x,
        y0: this.bounds.y0,
        y1: this.bounds.y1,
        fillcolor: COLORS.peakRemoveZone,
        line: {
          width: 0,
        },
        name: SHAPE_IDS.peakRemoveZone,
      } as const;
      log('enableRemovePeakMode: onPointerMove creating remove zone', shape);

      const [measurement] = this.readyForRenderMeasurements;
      const xMin = Math.min(shape.x0, shape.x1);
      const xMax = Math.max(shape.x0, shape.x1);
      log('enableRemovePeakMode: onPointerMove selection bounds', { xMin, xMax });
      this.renderOptions.highlightedPeakIds?.clear();
      const peaksInZone = measurement.peaks.filter((peak) => {
        const isTimeStartInside = peak.start > xMin && peak.start < xMax;
        const isTimeEndInside = peak.end > xMin && peak.end < xMax;
        const isTimeBothInside = peak.start < xMin && peak.end > xMax;
        return isTimeStartInside || isTimeEndInside || isTimeBothInside;
      });
      log('enableRemovePeakMode: onPointerMove peaks in zone', { count: peaksInZone.length });
      peaksInZone.forEach((peak) => this.renderOptions.highlightedPeakIds?.add(peak.id));

      this.addOrEditShape(shape);
      this.refreshImmediately();
    };
    const onPointerUp = () => {
      log('enableRemovePeakMode: onPointerUp', { isMouseDown });
      if (isMouseDown) {
        isMouseDown = false;
        startPosition = null;
        log('enableRemovePeakMode: onPointerUp removing zone shape');
        this.removeShape(SHAPE_IDS.peakRemoveZone);

        const peaksToDelete = [...(this.renderOptions.highlightedPeakIds ?? [])];
        log('enableRemovePeakMode: onPointerUp peaks to delete', {
          count: peaksToDelete.length,
          peaks: peaksToDelete,
        });
        this.renderOptions.highlightedPeakIds != null &&
          this.renderOptions.highlightedPeakIds?.size > 0 &&
          this.emitter.emit('deletePeaks', peaksToDelete);
        this.renderOptions.highlightedPeakIds?.clear();

        this.refreshImmediately();
      }
    };

    log('enableRemovePeakMode: adding chart listeners');
    const removeListeners = this.addChartListeners({
      onPointerDown,
      onPointerMove,
      onPointerUp,
    });

    this.disableCurrentMode = () => {
      log('enableRemovePeakMode: disableCurrentMode called');
      removeListeners();
    };
  }

  private enableBaselineMode() {
    this.disablePlotly();
    this.zoomActivationOverlay?.disable();
    this.removeShape(SHAPE_IDS.baseline);

    log('enableBaselineMode');
    let baselineCoords: Array<[x: number, y: number]> = [];
    log('enableBaselineMode: initial baseline coords', { length: baselineCoords.length });

    const refreshBaselinePreview = (e: PointerEvent) => {
      log('enableBaselineMode: refreshBaselinePreview');
      const { x, y } = this.getXY(e);
      const lastCoord = baselineCoords.at(-1);

      const isOutOfChart = this.isOutOfChart({ x, y });
      log('enableBaselineMode: refreshBaselinePreview state', {
        x,
        y,
        lastCoord,
        isOutOfChart,
        baselineCoordsLength: baselineCoords.length,
      });

      if (isOutOfChart && baselineCoords.length === 0) {
        log(
          'enableBaselineMode: refreshBaselinePreview removing baseline (out of chart, no coords)',
        );
        this.removeShape(SHAPE_IDS.baseline);
      } else {
        const coords = [
          [this.bounds.x0, baselineCoords.length !== 0 ? baselineCoords[0][1] : y] as const,
          ...baselineCoords,
          ...(lastCoord != null && (x < lastCoord[0] || this.isOutOfChart({ x, y }))
            ? ([[this.bounds.x1, lastCoord[1]]] as const)
            : [[x, y] as const, [this.bounds.x1, y] as const]),
        ];
        log('enableBaselineMode: refreshBaselinePreview coords', { coordsLength: coords.length });

        const shape = this.getBaselineShape(coords);
        this.addOrEditShape(shape);
      }

      this.refreshImmediately();
    };

    const addCoordToBaseline = (e: PointerEvent) => {
      log('enableBaselineMode: addCoordToBaseline');
      const { x, y } = this.getXY(e);
      const isOutOfChart = this.isOutOfChart({ x, y });
      const lastCoord = baselineCoords.at(-1);
      log('enableBaselineMode: addCoordToBaseline state', { x, y, isOutOfChart, lastCoord });

      if (isOutOfChart || x < Number(lastCoord?.[0])) {
        log('enableBaselineMode: addCoordToBaseline ignoring (out of chart or wrong order)');
        return;
      }

      log('enableBaselineMode: addCoordToBaseline adding coord', { x, y });
      baselineCoords.push([x, y]);
      log('enableBaselineMode: addCoordToBaseline emitting baseline', {
        coordsLength: baselineCoords.length,
      });
      this.emitter.emit('baseline', baselineCoords);
    };

    this.actions.resetBaseline = () => {
      log('enableBaselineMode: resetBaseline called');
      baselineCoords = [];
      this.removeShape(SHAPE_IDS.baseline);
      this.refreshImmediately();
      log('enableBaselineMode: resetBaseline finished');
    };
    this.actions.removeLastBaselineSegment = () => {
      log('enableBaselineMode: removeLastBaselineSegment called', {
        currentLength: baselineCoords.length,
      });
      baselineCoords.pop();

      if (baselineCoords.length === 0) {
        log('enableBaselineMode: removeLastBaselineSegment no coords left, removing shape');
        this.removeShape(SHAPE_IDS.baseline);
      } else {
        log('enableBaselineMode: removeLastBaselineSegment updating baseline shape');
        const firstCoord = baselineCoords.at(0);
        const lastCoord = baselineCoords.at(-1);
        if (firstCoord != null && lastCoord != null) {
          const coords = [
            [this.bounds.x0, firstCoord[1]] as const,
            ...baselineCoords,
            [this.bounds.x1, lastCoord[1]] as const,
          ];
          const shape = this.getBaselineShape(coords);
          this.addOrEditShape(shape);
        }
      }

      this.refreshImmediately();
      log('enableBaselineMode: removeLastBaselineSegment finished', {
        newLength: baselineCoords.length,
      });
    };

    const onKeyDown = (e: KeyboardEvent) => {
      log('enableBaselineMode: onKeyDown', { key: e.key });
      switch (e.key) {
        case 'Backspace': {
          log('enableBaselineMode: onKeyDown Backspace');
          this.actions.removeLastBaselineSegment?.();
          break;
        }
        case 'Escape': {
          log('enableBaselineMode: onKeyDown Escape');
          this.actions.resetBaseline?.();
          break;
        }
      }
    };

    const onPointerDown = (e: PointerEvent) => {
      log('enableBaselineMode: onPointerDown', { pointerType: e.pointerType });
      if (e.pointerType === 'mouse') {
        addCoordToBaseline(e);
      }
      if (e.pointerType === 'touch') {
        refreshBaselinePreview(e);
      }
    };
    const onPointerMove = (e: PointerEvent) => {
      log('enableBaselineMode: onPointerMove');
      refreshBaselinePreview(e);
    };
    const onPointerUp = (e: PointerEvent) => {
      log('enableBaselineMode: onPointerUp', { pointerType: e.pointerType });
      if (e.pointerType === 'touch') {
        addCoordToBaseline(e);
      }
    };

    log('enableBaselineMode: adding chart listeners');
    const removeListeners = this.addChartListeners({
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onKeyDown,
    });

    this.disableCurrentMode = () => {
      log('enableBaselineMode: disableCurrentMode called');
      removeListeners();

      this.actions.resetBaseline = null;
      this.actions.removeLastBaselineSegment = null;
      log('enableBaselineMode: disableCurrentMode finished');
    };
  }

  private getBaselineShape(coords: Array<readonly [x: number, y: number]>): Shape {
    log('getBaselineShape called', { coordsLength: coords.length });
    const shape: Shape = {
      type: 'path',
      path: convertCoordsToPath(coords),
      line: {
        color: COLORS.baselineAdd,
        width: 2,
      },
      name: SHAPE_IDS.baseline,
    };
    log('getBaselineShape result', {
      path: shape.path ? shape.path.substring(0, 100) + '...' : 'undefined',
    });
    return shape;
  }

  private addChartListeners(listeners: Partial<ChartEventListeners>): Callback {
    log('addChartListeners called', {
      hasPointerDown: !!listeners.onPointerDown,
      hasPointerMove: !!listeners.onPointerMove,
      hasPointerUp: !!listeners.onPointerUp,
      hasKeyDown: !!listeners.onKeyDown,
    });
    listeners.onPointerDown && this.chart.addEventListener('pointerdown', listeners.onPointerDown);
    // I use document here because plotly stops propagation for some events
    listeners.onPointerMove && document.addEventListener('pointermove', listeners.onPointerMove);
    listeners.onPointerUp && document.addEventListener('pointerup', listeners.onPointerUp);
    listeners.onKeyDown && document.addEventListener('keydown', listeners.onKeyDown);

    return () => {
      log('addChartListeners: removing listeners');
      listeners.onPointerDown &&
        this.chart.removeEventListener('pointerdown', listeners.onPointerDown);
      listeners.onPointerMove &&
        document.removeEventListener('pointermove', listeners.onPointerMove);
      listeners.onPointerUp && document.removeEventListener('pointerup', listeners.onPointerUp);
      listeners.onKeyDown && document.removeEventListener('keydown', listeners.onKeyDown);
    };
  }

  // TODO memoize?
  private getPeakShapes(_data: { peaks: Peak[]; data: DataExtended }) {
    log('getPeakShapes called', { peaksLength: _data.peaks.length, hasData: !!_data.data });
    const { data, peaks } = _data;

    if (data.y.length === 0) {
      log('getPeakShapes: no data, returning empty array');
      return [];
    }

    const allShapes = peaks
      .flatMap((peak, index) => {
        log('getPeakShapes: processing peak', {
          peakId: peak.id,
          index,
          start: peak.start,
          end: peak.end,
        });
        const xPeakStart = peak.start;
        const xPeakEnd = peak.end;
        const xPeakStartWithDTIMCorrection = xPeakStart - data.x0;
        const xPeakEndWithDTIMCorrection = xPeakEnd - data.x0;

        const getYAttachment = (xRel: number, xIndex: number): { xOnGraph: number; y: number } => {
          log('getPeakShapes: getYAttachment', { xRel, xIndex, dataLength: data.y.length });
          const xGraphMin = data.x0;
          const xGraphMax = data.x0 + (data.y.length - 1) * data.dx;

          if (xIndex < 0) {
            const result = {
              xOnGraph: xGraphMin,
              y: findYBetweenTwoPoints({
                x0: 0,
                x1: data.dx,
                y0: data.y[0] ?? throwErrorElementNotFound(),
                y1: data.y[1] ?? throwErrorElementNotFound(),
                xBetween: 0,
              }),
            };
            log('getPeakShapes: getYAttachment negative index', result);
            return result;
          }
          if (xIndex >= data.y.length - 1) {
            const lastYIndex = data.y.length - 1;
            const result = {
              xOnGraph: xGraphMax,
              y: findYBetweenTwoPoints({
                x0: (lastYIndex - 1) * data.dx,
                x1: lastYIndex * data.dx,
                y0: data.y[lastYIndex - 1] ?? throwErrorElementNotFound(),
                y1: data.y[lastYIndex] ?? throwErrorElementNotFound(),
                xBetween: lastYIndex * data.dx,
              }),
            };
            log('getPeakShapes: getYAttachment beyond data', result);
            return result;
          }
          const result = {
            xOnGraph: xPeakStart + (xRel - xPeakStartWithDTIMCorrection),
            y: findYBetweenTwoPoints({
              x0: xIndex * data.dx,
              x1: (xIndex + 1) * data.dx,
              y0: data.y[xIndex] ?? throwErrorElementNotFound(),
              y1: data.y[xIndex + 1] ?? throwErrorElementNotFound(),
              xBetween: xRel,
            }),
          };
          log('getPeakShapes: getYAttachment normal case', result);
          return result;
        };

        const peakShapes: Shape[] = [];

        if (
          !peak.is_integrated &&
          peak.fwhm_height != null &&
          peak.fwhm_start_time != null &&
          peak.fwhm_end_time != null &&
          peak.baseline_at_apex_mau != null
        ) {
          const xApexWithDTIMCorrection = peak.apex - data.x0;
          const apexIndex = Math.floor(xApexWithDTIMCorrection / data.dx);
          const { y: yApexOnMeasurement } = getYAttachment(xApexWithDTIMCorrection, apexIndex);

          const isHighlighted = this.renderOptions.highlightedPeakIds?.has(peak.id) ?? false;
          const crossColor = isHighlighted
            ? COLORS.peakCrossIndicatorHighlighted
            : COLORS.peakCrossIndicator;

          // Vertical line: from apex point on measurement to baseline
          // Only draw if baseline is below the peak apex
          const hasVerticalLine = peak.baseline_at_apex_mau <= yApexOnMeasurement;

          const verticalLine = hasVerticalLine
            ? ({
                type: 'line',
                x0: peak.apex,
                y0: yApexOnMeasurement,
                x1: peak.apex,
                y1: peak.baseline_at_apex_mau,
                line: {
                  color: crossColor,
                  width: 2,
                },
                name: `peak-${peak.id}-cross-vertical`,
              } as const)
            : null;

          const xFwhmStartRel = peak.fwhm_start_time - data.x0;
          const startIdx = Math.floor(xFwhmStartRel / data.dx);
          const { y: yFwhmStart } = getYAttachment(xFwhmStartRel, startIdx);

          const xFwhmEndRel = peak.fwhm_end_time - data.x0;
          const endIdx = Math.floor(xFwhmEndRel / data.dx);
          const { y: yFwhmEnd } = getYAttachment(xFwhmEndRel, endIdx);

          const horizontalLine = {
            type: 'line',
            x0: peak.fwhm_start_time,
            y0: yFwhmStart,
            x1: peak.fwhm_end_time,
            y1: yFwhmEnd,
            line: {
              color: crossColor,
              width: 2,
            },
            name: `peak-${peak.id}-cross-horizontal`,
          } as const;

          if (verticalLine) {
            peakShapes.push(verticalLine);
          }
          peakShapes.push(horizontalLine);
        }

        if (peak.is_integrated) {
          const _tempForXStart = Math.floor(xPeakStartWithDTIMCorrection / data.dx);
          const { xOnGraph: xStartOnGraph, y: yStartAttachmentToMeasurement } = getYAttachment(
            xPeakStartWithDTIMCorrection,
            _tempForXStart,
          );

          const _tempForXEnd = Math.floor(xPeakEndWithDTIMCorrection / data.dx);
          const { xOnGraph: xEndOnGraph, y: yEndAttachmentToMeasurement } = getYAttachment(
            xPeakEndWithDTIMCorrection,
            _tempForXEnd,
          );

          const yPeakStart =
            peak.start_mau == null ? yStartAttachmentToMeasurement : peak.start_mau;
          const yPeakEnd = peak.end_mau == null ? yEndAttachmentToMeasurement : peak.end_mau;
          log('getPeakShapes: peak coordinates calculated', {
            xStartOnGraph,
            xEndOnGraph,
            yPeakStart,
            yPeakEnd,
            yStartAttachmentToMeasurement,
            yEndAttachmentToMeasurement,
          });

          const line = {
            type: 'line',
            x0: xPeakStart,
            y0: yPeakStart,
            x1: xPeakEnd,
            y1: yPeakEnd,
            line: {
              color: peak.is_manual ? COLORS.peakLineManual : COLORS.peakLineAuto,
              width: 2,
            },
            name: `peak-${peak.id}`,
            label: { text: String(index + 1), textangle: 0 },
          } as const;

          log('getPeakShapes: created line shape', { peakId: peak.id, isManual: peak.is_manual });

          const perpendicularLeft = {
            type: 'line',
            x0: xStartOnGraph,
            y0: yPeakStart,
            x1: xStartOnGraph,
            y1: yStartAttachmentToMeasurement,
            line: { color: COLORS.peakPerpendicular, width: 2, dash: 'dot' },
            name: `peak-${peak.id}-perpendicularLeft`,
          } as const;

          const perpendicularRight = {
            type: 'line',
            x0: xEndOnGraph,
            y0: yPeakEnd,
            x1: xEndOnGraph,
            y1: yEndAttachmentToMeasurement,
            line: { color: COLORS.peakPerpendicular, width: 2, dash: 'dot' },
            name: `peak-${peak.id}-perpendicularRight`,
          } as const;

          peakShapes.push(perpendicularLeft, perpendicularRight);
          log('getPeakShapes: added perpendicular lines', { peakId: peak.id });

          const measurementPart: Array<[number, number]> = getMeasurementPart(
            data.y,
            data.dx,
            data.mps,
            data.x0,
            xStartOnGraph,
            xEndOnGraph,
          );
          log('getPeakShapes: measurement part calculated', {
            peakId: peak.id,
            measurementPartLength: measurementPart.length,
          });

          const coords: Array<[number, number]> = [
            [xStartOnGraph, yPeakStart],
            [xStartOnGraph, yStartAttachmentToMeasurement],
            ...measurementPart,
            [xEndOnGraph, yEndAttachmentToMeasurement],
            [xEndOnGraph, yPeakEnd],
          ];

          if (
            this.renderOptions.isShowEstimatedGaussians &&
            peak.estimated_shape === PEAK_ESTIMATED_SHAPES.SKEW_GAUSS &&
            peak.estimated_shape_params != null
          ) {
            log('getPeakShapes: creating estimated gaussian', { peakId: peak.id });
            const STEP = (1 / 60 / 1000) * 100;
            const xRange = generateRange(xStartOnGraph + STEP, xEndOnGraph - STEP, STEP);
            const estimatedPeak = generateSkewedGaussianRange(xRange, peak.estimated_shape_params);
            const rangeExtended = [xStartOnGraph, ...xRange, xEndOnGraph];
            const estimatedPeakExtended = [0, ...estimatedPeak, 0];

            const estimatedPeakCoords: [number, number][] = estimatedPeakExtended.map(
              (value, index) => {
                return [rangeExtended[index], value];
              },
            );

            const estimatedGaussian = {
              type: 'path',
              path: convertCoordsToPath(estimatedPeakCoords, true),
              fillcolor: COLORS.gaussianBackground,
              opacity: 1,
              line: {
                width: 1,
                color: COLORS.gaussianLine,
              },
              name: `peak-${peak.id}-estimated-gaussian`,
            } as const;

            peakShapes.push(estimatedGaussian);
            log('getPeakShapes: added estimated gaussian', { peakId: peak.id });
          }

          const background = {
            type: 'path',
            path: convertCoordsToPath(coords, true),
            fillcolor:
              this.mode === Modes.REMOVE_PEAK
                ? COLORS.peakHighlightForRemoving
                : COLORS.peakHighlight,
            opacity: this.renderOptions.highlightedPeakIds?.has(peak.id) ? 1 : 0,
            line: {
              width: 0,
            },
            name: `peak-${peak.id}-background`,
            onMouseEnter: () => {
              if (this.mode === Modes.DEFAULT) {
                log('getPeakShapes: peak mouse enter', { peakId: peak.id });
                this.emitter.emit('highlightPeak', peak.id);
              }
            },
            onMouseLeave: () => {
              if (this.mode === Modes.DEFAULT) {
                log('getPeakShapes: peak mouse leave', { peakId: peak.id });
                this.emitter.emit('highlightPeak', null);
              }
            },
            // onClick() {
            //   console.warn('ON CLICK');
            // },
          } as const;
          peakShapes.push(background, line);
          log('getPeakShapes: peak shapes completed', {
            peakId: peak.id,
            shapesCount: peakShapes.length,
            isHighlighted: this.renderOptions.highlightedPeakIds?.has(peak.id),
          });
        }

        return peakShapes;
      })
      .filter(Boolean);
    log('getPeakShapes finished', { totalShapesCount: allShapes.length });
    return allShapes;
  }

  private getOutOfDetectionTimeShapes(
    detectionTime: DetectionTime,
    expectedDurationSeconds: number,
  ): Shape[] {
    const IS_SHOW_FILL = true;
    const SHADE_FILL_COLOR = 'rgba(160,160,160,0.14)';
    const PAPER_Y_BOTTOM = 0;
    const PAPER_Y_TOP = 1;

    const chartXMin = 0;
    const chartXMax = expectedDurationSeconds / SECONDS_IN_MINUTE;
    const intervals: Array<[number, number]> = [];

    if (detectionTime.start != null && detectionTime.start > chartXMin) {
      intervals.push([chartXMin, detectionTime.start]);
    }
    if (detectionTime.end != null && detectionTime.end < chartXMax) {
      intervals.push([detectionTime.end, chartXMax]);
    }

    if (intervals.length === 0) {
      return [];
    }

    const shapes: Shape[] = [];

    if (IS_SHOW_FILL) {
      intervals.forEach(([x0, x1]) => {
        shapes.push({
          type: 'rect',
          xref: 'x',
          yref: 'paper',
          x0,
          x1,
          y0: PAPER_Y_BOTTOM,
          y1: PAPER_Y_TOP,
          fillcolor: SHADE_FILL_COLOR,
          line: { width: 0 },
          layer: 'below',
        } as const);
      });
    }

    return shapes;
  }

  private zoom(bounds: ZoomBounds) {
    log('zoom', bounds);
    Plotly.zoom(this.chart, bounds);
  }
}
